import React from 'react';
import { Star, BadgeCheck } from 'lucide-react';
import PartnerAvatar from './PartnerAvatar';

const CATEGORY_LABELS = {
  cbv: 'CBV',
  business_broker: 'BROKER',
  lender: 'LENDER',
  cepa_coach: 'ADVISOR',
  accountant: 'ACCOUNTANT',
  lawyer: 'LAWYER',
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-[14px] h-[14px] ${
              star <= Math.floor(rating || 0)
                ? 'text-[hsl(var(--succeedo-orange))] fill-[hsl(var(--succeedo-orange))]'
                : 'text-muted-foreground/20'
            }`}
          />
        ))}
      </div>
      {rating > 0 && (
        <span className="font-mono text-[11px] text-muted-foreground">{rating}</span>
      )}
    </div>
  );
}

export { StarRating, CATEGORY_LABELS };

export default function PartnerCard({ partner, onClick }) {
  const initials = (partner.name || '')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const categoryLabel = CATEGORY_LABELS[partner.partner_category] || partner.partner_category?.toUpperCase();
  const regions = (partner.geography_regions || []).join(', ');
  const industries = (partner.industry_tags || []).join('/');

  return (
    <div
      className="bg-card border border-border rounded-[16px] p-6 hover:border-border/50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      {/* Avatar */}
      <PartnerAvatar
        url={partner.avatar_url}
        name={partner.name}
        initials={initials}
        className="mb-4"
      />

      {/* Name + verified */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-[16px] font-semibold text-foreground">{partner.name}</h3>
        {partner.verified && (
          <BadgeCheck className="w-[14px] h-[14px] text-[hsl(var(--succeedo-orange))] flex-shrink-0 mt-1" />
        )}
      </div>

      {/* Category badge */}
      {categoryLabel && (
        <div className="inline-flex items-center font-mono text-[11px] uppercase rounded-full px-3 py-1 mb-3" style={{ color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.3)' }}>
          {categoryLabel}
        </div>
      )}

      {/* Firm */}
      {partner.org_name && (
        <div className="text-[13px] text-muted-foreground mb-2">{partner.org_name}</div>
      )}

      {/* Region + Industry */}
      {(regions || industries) && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {regions && <span className="font-mono text-[10px] uppercase text-muted-foreground/60">{regions}</span>}
          {regions && industries && <span className="text-muted-foreground/60">&middot;</span>}
          {industries && <span className="font-mono text-[10px] uppercase text-muted-foreground/60">{industries}</span>}
        </div>
      )}

      {/* Bio excerpt */}
      {partner.bio && (
        <p className="text-[13px] text-muted-foreground line-clamp-2 mb-4">{partner.bio}</p>
      )}

      {/* Rating */}
      {partner.rating > 0 && (
        <div className="mb-4">
          <StarRating rating={partner.rating} />
        </div>
      )}

      {/* CTA */}
      <div className="flex items-center gap-2">
        <button type="button" className="flex-1 border border-border text-foreground rounded-full px-4 py-2 text-sm text-center hover:bg-accent transition-colors">
          View Profile
        </button>
      </div>
    </div>
  );
}
